import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageContext } from '@/contexts/PackageContext';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import type { EntitlementFeatureKey } from '@/types/package';

/**
 * For entry points (menu items, buttons, tabs) that stay visible when the plan does not include the feature.
 *
 * `locked` comes from the SAME resolver the backend gates mirror (useEntitlement via useFeatureLock): new keys
 * lock only in enforce, legacy-enforced keys (canva, gdrive, brand_check, wordpress, whitelabel) lock whenever the
 * request gate would really block, in every mode. `decided` is false while the package is loading or failed to
 * load: callers must not show a lock (or the entry) then, so there is no locked flash and no false lock.
 * `onLockedClick` force-opens the existing upsell (an explicit click is never swallowed by the once-per-session guard).
 */
export const useLockedEntry = (feature: EntitlementFeatureKey) => {
  const { t } = useTranslation('common');
  const context = useContext(PackageContext);
  const lock = useFeatureLock(feature);
  // A failed package fetch counts as decided-unlocked (the backend still enforces; a click gets its refusal)
  const errored = !!context?.error;
  const decided = !!context && !context.isLoading && (!!context.data || errored);
  const locked = decided && !errored && lock.locked;
  const ariaLabelFor = (featureLabel: string): string => t('gating_locked_label', { feature: featureLabel });
  return {
    decided,
    locked,
    label: lock.label,
    upsellPlanSlug: lock.upsellPlanSlug,
    showUpsell: lock.showUpsell,
    ariaLabelFor,
    testId: `locked-entry-${feature}`,
  };
};

/**
 * True while the package/entitlements are not known yet (no data and no error). Pages that switch between the
 * real content and a locked card show their loading state meanwhile, so nothing mounts or fetches before the
 * decision. An errored package counts as decided (nothing is locked without a payload, as everywhere else).
 */
export const usePackagePending = (): boolean => {
  const context = useContext(PackageContext);
  if (!context) return false;
  return context.isLoading || (!context.data && !context.error);
};
